// LinkCardBlockEditor — Open Graph-style external link preview card.
//
// Admin enters a URL; optionally overrides title/description/image/siteName.
// При blur по полю URL мы делаем запрос /api/editorial/og-fetch и
// автоматически заполняем пустые поля. Если поле уже задано вручную —
// не перезаписываем.

import { useEffect, useRef, useState } from "react";
import type { LinkCardBlock, Block } from "../../../lib/blocks";
import { getApiBase, getToken } from "../../../lib/pb";
import { t } from "../theme";

export interface LinkCardBlockEditorProps {
  block: LinkCardBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
}

interface OgResponse {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
}

export function LinkCardBlockEditor({
  block,
  onChange,
  isSelected,
}: LinkCardBlockEditorProps) {
  let domain = "";
  try {
    if (block.url) domain = new URL(block.url).hostname;
  } catch {
    domain = "";
  }

  const [loading, setLoading] = useState(false);
  // Запоминаем последний URL, для которого уже сделали fetch, чтобы не
  // дёргать ручку повторно при каждом blur.
  const fetchedFor = useRef<string>("");

  async function fetchOg(url: string) {
    if (!url) return;
    if (fetchedFor.current === url) return;
    // Простейшая валидация — только http(s)://...
    if (!/^https?:\/\//i.test(url)) return;
    fetchedFor.current = url;
    setLoading(true);
    try {
      // og-fetch теперь требует editor/superuser auth (SSRF-гейт) — шлём токен.
      const token = getToken();
      const res = await fetch(
        `${getApiBase()}/api/editorial/og-fetch?url=${encodeURIComponent(url)}`,
        token ? { headers: { Authorization: token } } : undefined,
      );
      if (!res.ok) return;
      const data = (await res.json()) as OgResponse;
      const patch: Partial<LinkCardBlock> = {};
      if (data.title && !(block.title || "").trim()) patch.title = data.title;
      if (data.description && !(block.description || "").trim()) {
        patch.description = data.description;
      }
      if (data.image && !(block.image || "").trim()) patch.image = data.image;
      if (data.siteName && !(block.siteName || "").trim()) {
        patch.siteName = data.siteName;
      }
      if (Object.keys(patch).length > 0) {
        onChange(patch as Partial<Block>);
      }
    } catch {
      // silent — admin сможет ввести руками
    } finally {
      setLoading(false);
    }
  }

  // Авто-fetch при первом монтировании, если URL уже задан, а карточка
  // ещё пустая (например, юзер пришёл из markdown paste).
  useEffect(() => {
    if (
      block.url &&
      !block.title &&
      !block.description &&
      !block.image &&
      !block.siteName
    ) {
      fetchOg(block.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: 14,
          border: `1px solid ${t.border}`,
          borderRadius: 8,
          background: t.card,
          alignItems: "center",
        }}
      >
        {block.image && (
          <img
            src={block.image}
            alt=""
            style={{
              width: 96,
              height: 96,
              objectFit: "cover",
              borderRadius: 4,
              background: t.cardRaised,
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              color: t.text3,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 4,
              fontFamily: t.sans,
            }}
          >
            {block.siteName || domain || "Link"}
          </div>
          <div
            style={{
              fontFamily: t.serif,
              fontSize: 17,
              fontWeight: 600,
              color: t.text,
              lineHeight: 1.3,
              marginBottom: 4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {block.title || block.url || "Untitled link"}
          </div>
          {block.description && (
            <div
              style={{
                fontFamily: t.sans,
                fontSize: 12,
                color: t.text3,
                lineHeight: 1.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {block.description}
            </div>
          )}
        </div>
      </div>

      {isSelected && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 10,
            fontFamily: t.sans,
          }}
        >
          <div style={{ position: "relative" }}>
            <input
              type="url"
              value={block.url}
              onChange={(e) =>
                onChange({ url: e.target.value } as Partial<LinkCardBlock>)
              }
              onBlur={(e) => fetchOg(e.target.value.trim())}
              placeholder="https://example.com/article"
              style={input}
            />
            {loading && (
              <span
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 11,
                  color: t.text3,
                  pointerEvents: "none",
                }}
              >
                Fetching preview…
              </span>
            )}
          </div>
          <input
            type="text"
            value={block.title || ""}
            onChange={(e) =>
              onChange({ title: e.target.value } as Partial<LinkCardBlock>)
            }
            placeholder="Title (optional override)"
            style={input}
          />
          <input
            type="text"
            value={block.description || ""}
            onChange={(e) =>
              onChange({ description: e.target.value } as Partial<LinkCardBlock>)
            }
            placeholder="Description (optional)"
            style={input}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input
              type="url"
              value={block.image || ""}
              onChange={(e) =>
                onChange({ image: e.target.value } as Partial<LinkCardBlock>)
              }
              placeholder="Thumbnail URL (optional)"
              style={input}
            />
            <input
              type="text"
              value={block.siteName || ""}
              onChange={(e) =>
                onChange({ siteName: e.target.value } as Partial<LinkCardBlock>)
              }
              placeholder="Site name (optional)"
              style={input}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const input: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  background: t.bg,
  border: `1px solid ${t.border}`,
  borderRadius: 4,
  fontSize: 12,
  color: t.text,
  outline: "none",
  fontFamily: t.sans,
};
